import { useCallback, useEffect } from "react";

import type ReactModal from "react-modal";
import styled from "styled-components";

import { colorsPalettes, rgba } from "@similarweb/styles";
import { BasicModal, IconButton } from "@similarweb/ui-components";

import type { AdIntelligenceController } from "app/pages/digital-marketing/ad-intelligence/common/shared/types/types";
import { allTrackers } from "app/services/track/track";

import type { AdsGalleryData } from "../hooks/useAdsGallery";

import { AdModalContent } from "./AdModalContent";

const modalCustomStyles: ReactModal.Styles = {
    content: {
        width: 1200,
        height: 753,
        paddingTop: 24,
        paddingRight: 40,
        paddingLeft: 40,
        paddingBottom: 24,
    },
};

interface IAdModalProps {
    isOpen: boolean;
    currentSelectedAdIndex: number;
    data: AdsGalleryData[];
    onClose: () => void;
    onSelectedAdIndexChange: (index: number) => void;
    controller?: AdIntelligenceController;
    isOverviewContext?: boolean;
    industryName?: string;
}

const IconButtonStyled = styled(IconButton)<{ $location: "left" | "right" }>`
    z-index: 1;
    position: absolute;
    top: 50%;
    background-color: ${colorsPalettes.carbon[0]};
    box-shadow:
        0px 0px 8px 0px ${rgba(colorsPalettes.black[0], 0.12)},
        0px 8px 8px 0px ${rgba(colorsPalettes.black[0], 0.12)};
    ${({ $location }) => ($location === "left" ? "left: -20px;" : "right: -20px;")}

    &:hover {
        background-color: ${colorsPalettes.carbon[0]};
    }
`;

export const AdModal = ({
    isOpen,
    currentSelectedAdIndex,
    data,
    onClose,
    onSelectedAdIndexChange,
    controller = "advertiser",
    isOverviewContext = false,
    industryName,
}: IAdModalProps) => {
    const hasPrev = currentSelectedAdIndex > 0;
    const hasNext = currentSelectedAdIndex < data.length - 1;

    const goToNextAd = useCallback(() => {
        onSelectedAdIndexChange(currentSelectedAdIndex + 1);
        allTrackers.dispatchTrackEvent("Pop Up", "click", "Ads/Next");
    }, [currentSelectedAdIndex, onSelectedAdIndexChange]);

    const goToPrevAd = useCallback(() => {
        onSelectedAdIndexChange(currentSelectedAdIndex - 1);
        allTrackers.dispatchTrackEvent("Pop Up", "click", "Ads/Previous");
    }, [currentSelectedAdIndex, onSelectedAdIndexChange]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "ArrowRight" && hasNext) {
                goToNextAd();
            } else if (e.key === "ArrowLeft" && hasPrev) {
                goToPrevAd();
            }
        };

        if (isOpen) {
            document.addEventListener("keydown", handleKeyDown);
        }

        return () => {
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [goToNextAd, goToPrevAd, hasNext, hasPrev, isOpen]);

    const handleClose = () => {
        onClose();
        allTrackers.dispatchTrackEvent("Pop Up", "close", "Ads/More info");
    };

    return (
        <BasicModal
            isOpen={isOpen}
            closeIconConfig={{ show: false }}
            customStyles={modalCustomStyles}
            onRequestClose={handleClose}
            shouldCloseOnOverlayClick={true}
        >
            {hasPrev ? (
                <IconButtonStyled
                    iconName="arrow-left"
                    $location="left"
                    iconSize="sm"
                    type="flat"
                    onClick={goToPrevAd}
                    data-automation="ad-modal-prev-button"
                />
            ) : null}
            <AdModalContent
                onClose={handleClose}
                controller={controller}
                isOverviewContext={isOverviewContext}
                industryName={industryName}
                {...data[currentSelectedAdIndex]}
            />
            {hasNext ? (
                <IconButtonStyled
                    iconName="arrow-right"
                    $location="right"
                    iconSize="sm"
                    type="flat"
                    onClick={goToNextAd}
                    data-automation="ad-modal-next-button"
                />
            ) : null}
        </BasicModal>
    );
};
